// Eskom PARCC — the bulk-import UPDATE whitelist (WS2, 1 Jul 2026 incident).
//
// Dependency-free on purpose: both the Firebase-bound write layer (dbWrite's
// transactionImportUpdateVars) and the pure parser (importParse's
// isUnchangedForImport) consume this list, and the parser must stay importable
// without dragging in the Firebase singleton (tests mock dbWrite wholesale).

/**
 * The columns a bulk RE-import may write onto an EXISTING transaction — source/
 * population data only. This is the import path's blast radius: reviewing this
 * list IS reviewing what an upload can ever overwrite.
 *
 * Deliberately absent: `reviewed`, `reviewStatus`, `progress`, `workingPaperNotes`,
 * `findingText`, `complianceLevelOverride` (review/work-product state — the 15:15
 * clobber class); `assignedToEmail`, `assignedByEmail`, `reviewerEmail`, `assignedAt`
 * (allocation is owned by the allocate flow); `onDashboard` (curation flag); and
 * `gapsStatus` + `complianceLevel` (file-borne on FIRST import, review outputs
 * thereafter — they still land on INSERT via `transactionInsertVars`, never on
 * update).
 */
export const IMPORT_UPDATE_COLUMNS = [
  'dataset',
  'division',
  'supplier',
  'csdNumber',
  'value',
  'valueDisplay',
  'mechanism',
  'link',
  'ou',
  'contractNumber',
  'cirNumber',
  'linkProvided',
  'linkIssue',
  'panel',
  'bu',
  'briefDescription',
  'buyer',
  'prNumber',
  'procurementManager',
  'endUser',
  'selfDeclaration',
  'initialFindings',
  'updatedFindings',
] as const;

/** One column of the import-update whitelist. */
export type ImportUpdateColumn = (typeof IMPORT_UPDATE_COLUMNS)[number];

/**
 * The columns the BULK UPDATE screen may write onto an existing transaction —
 * the full surface a person can reach through the manual editors, per Lindiwe's
 * R6 ask ("the same mechanism as a reviewer manually updating a transaction,
 * except done via bulk Excel"). Reviewing this list IS reviewing what one
 * spreadsheet confirm can change.
 *
 * It is deliberately WIDER than {@link IMPORT_UPDATE_COLUMNS}, and the safety
 * comes from the screen, not the whitelist:
 *   1. only columns PHYSICALLY PRESENT in the uploaded file are ever emitted —
 *      an absent column is omitted, so the row keeps its value, and a blank cell
 *      in a present column is a no-op, never a clear;
 *   2. every changed cell is shown old → new and confirmed before any write;
 *   3. findings MERGE (analyst/CES lines preserved), they are never replaced;
 *   4. `gapsStatus` is RE-DERIVED from the findings after a merge, never taken
 *      from the sheet — it is a rollup, so a written value would be overwritten
 *      by the next save anyway;
 *   5. `reviewed` and `reviewStatus` move together via reviewedFlagForStatus.
 *
 * Deliberately absent — owned by the allocate flow and the removal machinery, and
 * never movable from a spreadsheet: `assignedToEmail`, `assignedByEmail`,
 * `assignedAt`, `reviewerEmail`, `onDashboard`, `removalBatchId`, `deleted`.
 * Also absent by construction: `referenceNo`, `baseNo`, `recordLabel` and `year`
 * are insert-only at the connector (see TransactionUpdateInput), so no mapper
 * can emit them however the file is shaped.
 */
export const BULK_UPDATE_COLUMNS = [
  // Population / source columns — the same set the import may write, plus
  // `period` (updatable since the Aug 2026 owner feedback: "unable to update
  // period" on Manage or the bulk update; here as on Manage, only ever a
  // correction, never a clear). It stays OFF the import whitelist: a
  // re-imported population file must not silently move it.
  'dataset',
  'period',
  'division',
  'supplier',
  'csdNumber',
  'value',
  'valueDisplay',
  'mechanism',
  'link',
  'ou',
  'contractNumber',
  'cirNumber',
  'linkProvided',
  'linkIssue',
  'panel',
  'bu',
  'briefDescription',
  'buyer',
  'prNumber',
  'procurementManager',
  'endUser',
  'selfDeclaration',
  'initialFindings',
  'updatedFindings',
  // Review / work-product columns — the manual-editor surface (TK, R6).
  'findingText',
  'complianceLevel',
  'complianceLevelOverride',
  'reviewStatus',
  'reviewed',
  'gapsStatus',
] as const;

/** One column of the bulk-update whitelist. */
export type BulkUpdateColumn = (typeof BULK_UPDATE_COLUMNS)[number];
